<template>
  <div>
    <div class="modal-body">
      <form @submit.prevent="handleSubmit">
        <!-- Step Indicator -->
        <div class="mb-4 text-center">
          <span :class="['step-badge', step === 1 ? 'active' : '']">1</span>
          <span class="mx-2">—</span>
          <span :class="['step-badge', step === 2 ? 'active' : '']">2</span>
        </div>

        <!-- STEP 1 -->
        <div v-if="step === 1">
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <div class="mb-3">
            <label class="form-label">Grade</label>
            <select
              class="form-select"
              v-model="newTopicEnrollment.grade_id"
              required
            >
              <option value="" disabled>Select Grade</option>
              <option v-for="g in grades" :key="g.id" :value="g.id">
                {{ g.level }}
              </option>
            </select>
          </div>

          <!-- Subject -->
          <div class="mb-3">
            <label class="form-label">Subject</label>
            <select
              class="form-select"
              v-model="newTopicEnrollment.subject_id"
              :disabled="!newTopicEnrollment.grade_id"
              required
            >
              <option value="" disabled>Select Subject</option>
              <option v-for="s in subjects" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- STEP 2 -->
        <div v-if="step === 2">
          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <!-- Students -->
          <div class="mb-3">
            <label class="form-label">Student</label>
            <select
              class="form-select"
              v-model="newTopicEnrollment.student_id"
              :disabled="
                !newTopicEnrollment.grade_id || !newTopicEnrollment.subject_id
              "
              required
            >
              <option value="" disabled>Select Student</option>
              <option
                v-for="student in students"
                :key="student.id"
                :value="student.id"
              >
                {{ student.first_name }} {{ student.last_name }}
              </option>
            </select>
          </div>

          <!-- Topic -->
          <div class="mb-3">
            <label class="form-label">Topic</label>
            <select
              class="form-select"
              v-model="newTopicEnrollment.topic_id"
              :disabled="!newTopicEnrollment.subject_id"
              required
            >
              <option value="" disabled>Select Topic</option>
              <option v-for="t in topics" :key="t.id" :value="t.id">
                {{ t.title }}
              </option>
            </select>
          </div>

          <!-- Month -->
          <div class="mb-3">
            <label class="form-label">Month</label>
            <select
              class="form-select"
              v-model="newTopicEnrollment.month"
              required
            >
              <option value="" disabled>Select Month</option>
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer p-0 mt-3">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>

          <button
            v-if="step === 2"
            type="button"
            class="btn btn-outline-secondary"
            @click="step--"
          >
            Back
          </button>

          <button
            v-if="step === 1"
            type="button"
            class="btn btn-primary"
            @click="goToStep2"
          >
            Next
          </button>

          <button
            v-if="step === 2"
            type="submit"
            class="btn btn-pink"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ loading ? "Saving..." : "Save" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AddVideo",

  data() {
    return {
      step: 1,
      grades: [],
      subjects: [],
      students: [],
      topics: [],
      loading: false,
      errorMessage: "",
      successMessage: "", // <-- add this

      months: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],

      newTopicEnrollment: {
        grade_id: "",
        subject_id: "",
        topic_id: "",
        student_id: "",
        month: "",
        status: "active",
      },
    };
  },

  mounted() {
    this.fetchGrades();
  },

  watch: {
    "newTopicEnrollment.grade_id"(newVal) {
      this.newTopicEnrollment.subject_id = "";
      this.newTopicEnrollment.topic_id = "";
      this.newTopicEnrollment.student_id = "";

      this.subjects = [];
      this.topics = [];
      this.students = []; // ✅ reset students

      if (newVal) this.fetchSubjectsByGrade();
    },

    "newTopicEnrollment.subject_id"(newVal) {
      this.newTopicEnrollment.topic_id = "";
      this.newTopicEnrollment.student_id = "";

      this.topics = [];
      this.students = [];

      if (newVal) {
        this.fetchTopicsBySubject();
        this.fetchStudentsByGradeSubject(); // ✅ load students
      }
    },
  },

  methods: {
    goToStep2() {
      if (
        !this.newTopicEnrollment.grade_id ||
        !this.newTopicEnrollment.subject_id
      ) {
        this.errorMessage = "Please select a grade and subject first.";
        return;
      }
      this.errorMessage = "";
      this.step = 2;
    },

    handleSubmit() {
      if (this.step === 2) {
        this.addStudentTopicEnrollment();
      }
    },

    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchSubjectsByGrade() {
      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.newTopicEnrollment.grade_id}`,
        );
        // Flatten subjects if nested
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
      }
    },

    async fetchStudentsByGradeSubject() {
      if (
        !this.newTopicEnrollment.grade_id ||
        !this.newTopicEnrollment.subject_id
      )
        return;

      try {
        const res = await axios.get("/students-by-grade-subject", {
          params: {
            grade_id: this.newTopicEnrollment.grade_id,
            subject_id: this.newTopicEnrollment.subject_id,
          },
        });
        this.students = res.data;
      } catch (err) {
        console.error(err);
        this.students = [];
      }
    },

    async fetchTopicsBySubject() {
      try {
        const res = await axios.get(
          `/a-get-subject-topics?subject_id=${this.newTopicEnrollment.subject_id}`,
        );
        this.topics =
          res.data.topics ||
          res.data.map((t) => ({ id: t.id, title: t.title }));
      } catch (err) {
        console.error(err);
      }
    },

    async addStudentTopicEnrollment() {
      this.loading = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const res = await axios.post(
          "/topic-enrollments",
          this.newTopicEnrollment,
        );

        this.successMessage = res.data.message || "Enrollment successful!";

        this.$emit("topic-enrollment-added", res.data);

        // Reset form after success
        this.resetForm();

        // Optional: close modal automatically
        // document.querySelector("#yourModalId .btn-close")?.click();
      } catch (err) {
        if (err.response && err.response.data.message) {
          this.errorMessage = err.response.data.message;
        } else {
          this.errorMessage = "Something went wrong. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.step = 1;
      this.subjects = [];
      this.topics = [];
      this.newTopicEnrollment = {
        grade_id: "",
        subject_id: "",
        topic_id: "",
        month: "",
        status: "active",
      };
    },
  },
};
</script>

<style scoped lang="scss">
.step-badge {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ddd;
  line-height: 30px;
  font-weight: bold;
}
.step-badge.active {
  background: #e83e8c;
  color: white;
}

.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}
</style>
